import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface CachedAuthState {
  user: User | null;
  session: Session | null;
  profile: any | null;
  timestamp: number;
  expiresAt: number;
}

interface CachedProfile {
  id: string;
  first_name?: string;
  last_name?: string;
  gender?: string;
  handicap?: number;
  avatar_url?: string;
  timestamp: number;
}

/**
 * Security: Validate cached auth state structure to prevent cache poisoning attacks
 * If localStorage is modified by malicious scripts, we reject malformed data
 */
function isValidCachedAuthState(data: unknown): data is CachedAuthState {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  // Required fields with correct types
  if (typeof obj.timestamp !== 'number') return false;
  if (typeof obj.expiresAt !== 'number') return false;

  // User can be null or must have id
  if (obj.user !== null) {
    if (typeof obj.user !== 'object') return false;
    const user = obj.user as Record<string, unknown>;
    if (typeof user.id !== 'string') return false;
  }

  return true;
}

/**
 * Security: Validate cached profile structure
 */
function isValidCachedProfile(data: unknown): data is CachedProfile {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  if (typeof obj.id !== 'string') return false;
  if (typeof obj.timestamp !== 'number') return false;

  return true;
}

class AuthCacheService {
  private readonly AUTH_CACHE_KEY = 'auth_state_cache';
  private readonly PROFILE_CACHE_KEY = 'profile_cache';
  // SECURITY: Reduced from 30 minutes to 5 minutes to limit session exposure window
  // See: docs/security/phases/01-threat-modeling/2026-04-21-threat-model.md (TM-003)
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Cache authentication state
  cacheAuthState(user: User | null, session: Session | null, profile: any = null): void {
    try {
      const expiresAt = Date.now() + this.CACHE_DURATION;
      const cacheData: CachedAuthState = {
        user,
        session,
        profile,
        timestamp: Date.now(),
        expiresAt
      };
      
      localStorage.setItem(this.AUTH_CACHE_KEY, JSON.stringify(cacheData));
      
      if (import.meta.env.DEV) {
        console.log('Auth state cached', { hasUser: !!user, hasSession: !!session, hasProfile: !!profile });
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to cache auth state:', error);
    }
  }

  // Get cached authentication state
  getCachedAuthState(): CachedAuthState | null {
    try {
      const cached = localStorage.getItem(this.AUTH_CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);

      // SECURITY: Validate structure to prevent cache poisoning attacks
      if (!isValidCachedAuthState(data)) {
        console.warn('[AuthCache] Invalid cache structure detected, clearing');
        this.clearAuthCache();
        return null;
      }

      // Check if cache is expired
      if (Date.now() > data.expiresAt) {
        this.clearAuthCache();
        return null;
      }

      if (import.meta.env.DEV) {
        console.log('Auth state retrieved from cache', {
          hasUser: !!data.user,
          hasSession: !!data.session,
          age: Date.now() - data.timestamp
        });
      }

      return data;
    } catch (error) {
      // SECURITY: On parse error, clear potentially corrupted cache
      this.clearAuthCache();
      if (import.meta.env.DEV) console.warn('Failed to get cached auth state:', error);
      return null;
    }
  }

  // Cache user profile data
  cacheProfile(profile: any): void {
    if (!profile?.id) return;

    try {
      const cacheData: CachedProfile = {
        ...profile,
        timestamp: Date.now()
      };
      
      localStorage.setItem(`${this.PROFILE_CACHE_KEY}_${profile.id}`, JSON.stringify(cacheData));
      
      if (import.meta.env.DEV) {
        console.log('Profile cached for user:', profile.id);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to cache profile:', error);
    }
  }

  // Get cached profile data
  getCachedProfile(userId: string): CachedProfile | null {
    if (!userId) return null;

    try {
      const cached = localStorage.getItem(`${this.PROFILE_CACHE_KEY}_${userId}`);
      if (!cached) return null;

      const data = JSON.parse(cached);

      // SECURITY: Validate structure to prevent cache poisoning attacks
      if (!isValidCachedProfile(data)) {
        console.warn('[AuthCache] Invalid profile cache structure detected, clearing');
        localStorage.removeItem(`${this.PROFILE_CACHE_KEY}_${userId}`);
        return null;
      }

      // Check if cache is expired
      if (Date.now() - data.timestamp > this.PROFILE_CACHE_DURATION) {
        localStorage.removeItem(`${this.PROFILE_CACHE_KEY}_${userId}`);
        return null;
      }

      if (import.meta.env.DEV) {
        console.log('Profile retrieved from cache for user:', userId, {
          age: Date.now() - data.timestamp
        });
      }

      return data;
    } catch (error) {
      // SECURITY: On parse error, clear potentially corrupted cache
      localStorage.removeItem(`${this.PROFILE_CACHE_KEY}_${userId}`);
      if (import.meta.env.DEV) console.warn('Failed to get cached profile:', error);
      return null;
    }
  }

  // Preload user profile data
  async preloadProfile(userId: string): Promise<any | null> {
    if (!userId) return null;

    // Check cache first
    const cached = this.getCachedProfile(userId);
    if (cached) return cached;

    try {
      if (import.meta.env.DEV) {
        console.log('Preloading profile for user:', userId);
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, gender, handicap, avatar_url')
        .eq('id', userId)
        .single();

      if (error) {
        if (import.meta.env.DEV) console.warn('Failed to preload profile:', error);
        return null;
      }

      // Cache the loaded profile
      if (profile) {
        this.cacheProfile(profile);
      }

      return profile;
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Error preloading profile:', error);
      return null;
    }
  }

  // Optimize social auth redirects by pre-storing state
  optimizeSocialAuth(provider: string, redirectTo: string): void {
    try {
      const socialAuthState = {
        provider,
        redirectTo,
        timestamp: Date.now(),
        state: crypto.randomUUID()
      };

      sessionStorage.setItem('social_auth_state', JSON.stringify(socialAuthState));
      
      if (import.meta.env.DEV) {
        console.log('Social auth state optimized for provider:', provider);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to optimize social auth:', error);
    }
  }

  // Get social auth optimization data
  getSocialAuthState(): any | null {
    try {
      const cached = sessionStorage.getItem('social_auth_state');
      if (!cached) return null;

      const data = JSON.parse(cached);
      
      // Clean up after retrieval
      sessionStorage.removeItem('social_auth_state');
      
      return data;
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to get social auth state:', error);
      return null;
    }
  }

  // Clear authentication cache
  clearAuthCache(): void {
    try {
      localStorage.removeItem(this.AUTH_CACHE_KEY);
      if (import.meta.env.DEV) console.log('Auth cache cleared');
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to clear auth cache:', error);
    }
  }

  // Clear profile cache
  clearProfileCache(userId?: string): void {
    try {
      if (userId) {
        localStorage.removeItem(`${this.PROFILE_CACHE_KEY}_${userId}`);
        if (import.meta.env.DEV) console.log('Profile cache cleared for user:', userId);
      } else {
        // Clear all profile caches
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(this.PROFILE_CACHE_KEY)) {
            localStorage.removeItem(key);
          }
        });
        if (import.meta.env.DEV) console.log('All profile caches cleared');
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to clear profile cache:', error);
    }
  }

  // Clear all caches
  clearAllCache(): void {
    this.clearAuthCache();
    this.clearProfileCache();
  }

  // Check if auth state is cached and valid
  isAuthCached(): boolean {
    const cached = this.getCachedAuthState();
    return cached !== null && !!cached.user;
  }

  // Warm up authentication by checking cache and session
  async warmUpAuth(): Promise<{ user: User | null; session: Session | null; profile: any | null }> {
    // First, check cache
    const cached = this.getCachedAuthState();
    if (cached) {
      return {
        user: cached.user,
        session: cached.session,
        profile: cached.profile
      };
    }

    // If not cached, get fresh session
    try {
      const { data: { user, session }, error } = await supabase.auth.getUser();
      
      if (error || !user) {
        return { user: null, session: null, profile: null };
      }

      // Preload profile data
      const profile = await this.preloadProfile(user.id);

      // Cache the fresh data
      this.cacheAuthState(user, session, profile);

      return { user, session, profile };
    } catch (error) {
      if (import.meta.env.DEV) console.warn('Failed to warm up auth:', error);
      return { user: null, session: null, profile: null };
    }
  }
}

export const authCacheService = new AuthCacheService();